/* eslint-disable no-param-reassign */
import React, { ReactNode } from 'react';
import { Editor, Transforms, Element as SlateElement } from 'slate';
import {
	AligmentFormat,
	BaseBlockFormat,
	BlockFormat,
	CustomEditor,
	CustomElement,
	CustomText,
	EmbedBlockFormat,
	FloatFormat,
	ListFormat,
	MarkFormat,
	RenderElementProps,
	SIZE_MAP,
	TableFormat,
	TwoColumnsElement,
} from 'components/_new/NewSlateEditor/types';
import Image from '../Elements/Embed/Image';
import Video from '../Elements/Embed/Video';
import { LinkEditor } from '../Elements/Link/LinkElement';
import { Columns } from '../Elements/Columns/Columns';
import { TableEditor } from '../Elements/Table/TableEditor';

const ALIGMENT = Object.values(AligmentFormat);
const LIST_TYPES = Object.values(ListFormat);

export const toggleBlock = (editor: CustomEditor, format: BlockFormat) => {
	const isActive = isBlockActive(editor, format);
	const isList = LIST_TYPES.includes(format as ListFormat);
	const isIndent = ALIGMENT.includes(format as AligmentFormat);
	const isAligned = ALIGMENT.some((alignmentType) => isBlockActive(editor, alignmentType));
	// const isBlock = BLOCK_TYPES.includes(format);

	/* If the node is already aligned and change in indent is called we should unwrap it first and split the node to prevent
    messy, nested DOM structure and bugs due to that. */
	if (isAligned && isIndent) {
		Transforms.unwrapNodes(editor, {
			match: (n) =>
				!Editor.isEditor(n) && SlateElement.isElement(n) && ALIGMENT.includes(n.type as AligmentFormat),
			split: true,
		});
	}

	/* Wraping the nodes for alignment, to allow it to co-exist with other block level operations */
	if (isIndent) {
		Transforms.wrapNodes(editor, {
			type: format,
			children: [],
		} as CustomElement);
		return;
	}

	if (format === BaseBlockFormat.TWO_COLUMNS) {
		if (isActive) {
			Transforms.unwrapNodes(editor, {
				match: (n: any) => (n as any).type === BaseBlockFormat.TWO_COLUMNS,
				split: true,
			});
		} else {
			insertBlock(editor, BaseBlockFormat.TWO_COLUMNS);
		}
		return;
	}

	Transforms.unwrapNodes(editor, {
		match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && LIST_TYPES.includes(n.type as ListFormat),
		split: true,
	});

	if (isActive) {
		Transforms.setNodes(editor, {
			type: BaseBlockFormat.PARAGRAPH,
		});
	} else {
		Transforms.setNodes(editor, {
			type: isList ? ListFormat.ITEM : format,
		});
	}

	if (isList && !isActive) {
		Transforms.wrapNodes(editor, {
			type: format,
			children: [],
		} as CustomElement);
	}
};

export const insertBlock = (editor: Editor, format: BlockFormat) => {
	const isActive = isBlockActive(editor, format);
	if (!isActive && format === BaseBlockFormat.TWO_COLUMNS) {
		if (isActive) {
			Transforms.unwrapNodes(editor, {
				match: (n: any) => (n as any).type === BaseBlockFormat.TWO_COLUMNS,
				split: true,
			});
		} else {
			const twoColumnsNode: TwoColumnsElement = {
				type: BaseBlockFormat.TWO_COLUMNS,
				children: [
					{
						type: 'column',
						children: [{ text: 'Wprowadź treść dla lewej strony...' }],
					},
					{
						type: 'column',
						children: [{ text: 'Wprowadź treść dla prawej strony...' }],
					},
				],
			};
			Transforms.insertNodes(editor, twoColumnsNode, { select: false });
		}
	}
};

export const addMarkData = (editor: CustomEditor, format: MarkFormat, value: string) => {
	Editor.addMark(editor, format, value);
};

export const toggleMark = (editor: CustomEditor, format: MarkFormat) => {
	const isActive = isMarkActive(editor, format);
	if (isActive) {
		Editor.removeMark(editor, format);
	} else {
		Editor.addMark(editor, format, true);
	}
};

export const isMarkActive = (editor: CustomEditor, format: MarkFormat) => {
	const marks = Editor.marks(editor);
	return marks ? marks[format] === true : false;
};

export const isBlockActive = (editor: CustomEditor, format: BlockFormat) => {
	const [match] = Editor.nodes(editor, {
		match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
	});
	return !!match;
};

export const activeMarkValue = (
	editor: CustomEditor,
	format: MarkFormat.BACKGROUND_COLOR | MarkFormat.COLOR | MarkFormat.FONT_SIZE
) => {
	const defaultMarkData: Partial<Record<MarkFormat, string>> = {
		color: 'black',
		backgroundColor: 'white',
		fontSize: 'normal',
		// 	fontFamily: 'sans',
	};
	const marks = Editor.marks(editor);
	const defaultValue = defaultMarkData[format];
	return marks?.[format] ?? defaultValue;
};

export const activeMarkBoolean = (editor: CustomEditor, format: MarkFormat) => {
	const marks = Editor.marks(editor);
	return Boolean(marks?.[format] ?? false);
};

export const getMarked = (leaf: CustomText, children: ReactNode) => {
	if (leaf.bold) {
		children = <strong>{children}</strong>;
	}
	if (leaf.italic) {
		children = <em>{children}</em>;
	}
	if (leaf.strikethrough) {
		children = <span style={{ textDecoration: 'line-through' }}>{children}</span>;
	}
	if (leaf.underline) {
		children = <u>{children}</u>;
	}
	if (leaf.superscript) {
		children = <sup>{children}</sup>;
	}
	if (leaf.subscript) {
		children = <sub>{children}</sub>;
	}
	if (leaf.color) {
		children = <span style={{ color: leaf.color }}>{children}</span>;
	}
	if (leaf.backgroundColor) {
		children = <span style={{ backgroundColor: leaf.backgroundColor }}>{children}</span>;
	}
	if (leaf.fontSize) {
		const size = SIZE_MAP[leaf.fontSize];
		children = <span style={{ fontSize: size }}>{children}</span>;
	}
	// if (leaf.fontFamily) {
	// 	const family = FONT_FAMILY_MAP[leaf.fontFamily];
	// 	children = <span style={{ fontFamily: family }}>{children}</span>;
	// }
	return children;
};

export const getBlock = (props: RenderElementProps) => {
	const { element, children } = props;
	const attributes = props.attributes ?? {};

	switch (element.type) {
		case BaseBlockFormat.HEADING_ONE:
			return (
				<h1 {...attributes} {...element.attr}>
					{children}
				</h1>
			);
		case BaseBlockFormat.HEADING_TWO:
			return (
				<h2 {...attributes} {...element.attr}>
					{children}
				</h2>
			);
		case BaseBlockFormat.HEADING_THREE:
			return (
				<h3 {...attributes} {...element.attr}>
					{children}
				</h3>
			);
		case BaseBlockFormat.BLOCKQUOTE:
			return (
				<blockquote {...attributes} {...element.attr}>
					{children}
				</blockquote>
			);
		case AligmentFormat.LEFT:
			return (
				<div style={{ listStylePosition: 'inside' }} {...attributes} {...element.attr}>
					{children}
				</div>
			);
		case AligmentFormat.CENTER:
			return (
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						listStylePosition: 'inside',
						flexDirection: 'column',
					}}
					{...attributes}
					{...element.attr}
				>
					{children}
				</div>
			);
		case AligmentFormat.RIGHT:
			return (
				<div
					style={{
						display: 'flex',
						alignItems: 'flex-end',
						listStylePosition: 'inside',
						flexDirection: 'column',
					}}
					{...attributes}
					{...element.attr}
				>
					{children}
				</div>
			);
		case ListFormat.ORDERED:
			return (
				<ol type="1" {...attributes}>
					{children}
				</ol>
			);
		case ListFormat.UNORDERED:
			return <ul {...attributes}>{children}</ul>;
		case ListFormat.ITEM:
			return (
				<li {...attributes} {...element.attr}>
					{children}
				</li>
			);
		case BaseBlockFormat.LINK:
			return (
				<LinkEditor element={element} attributes={attributes}>
					{children}
				</LinkEditor>
			);
		case EmbedBlockFormat.IMAGE:
			return (
				<Image element={element} attributes={attributes}>
					{children}
				</Image>
			);
		case EmbedBlockFormat.VIDEO:
			return (
				<Video element={element} attributes={attributes}>
					{children}
				</Video>
			);
		case BaseBlockFormat.TWO_COLUMNS:
			return <Columns {...props} />;
		case TableFormat.TABLE:
			return <TableEditor attributes={attributes}>{children}</TableEditor>;
		case TableFormat.TABLE_ROW:
			return <tr>{children}</tr>;
		case TableFormat.TABLE_CELL:
			return <td>{children}</td>;
		case BaseBlockFormat.PARAGRAPH:
			return <p {...attributes}>{children}</p>;
		case FloatFormat.FLOAT_LEFT:
			return (
				<div className="float-left" {...attributes}>
					{children}
				</div>
			);
		case FloatFormat.FLOAT_RIGHT:
			return (
				<div className="float-right" {...attributes}>
					{children}
				</div>
			);
		case FloatFormat.CLEAR_BOTH:
			return (
				<div className="clear-both" {...attributes}>
					{children}
				</div>
			);
		default:
			return <p>{children || ''}</p>;
	}
};
